import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Moon, Sun, Download, Zap, LogOut } from 'lucide-react';

export default function Header() {
    const { theme, toggle } = useTheme();
    const auth = useAuth() || {};
    const cfg = useAppConfig();
    const { user, logout } = auth;
    const nav = useNavigate();
    const loc = useLocation();
    const [installPrompt, setInstallPrompt] = useState(null);

    useEffect(() => {
        const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (!installPrompt) return;
        installPrompt.prompt();
        await installPrompt.userChoice;
        setInstallPrompt(null);
    };

    const hideOnPortal = loc.pathname.startsWith('/p/');
    if (hideOnPortal) return null;

    return (
        <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
            <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
                <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2">
                    <div className="h-8 w-8 grid place-items-center bg-[#FF6B35] text-white">
                        <Zap className="h-4 w-4" strokeWidth={2.5} />
                    </div>
                    <span className="font-heading text-xl font-semibold tracking-tight">Magnidrop</span>
                    {cfg.beta_active && (
                        <span
                            data-testid="brand-beta-tag"
                            className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B35]/80 font-medium ml-0.5"
                        >
                            (Beta)
                        </span>
                    )}
                </Link>

                <nav className="hidden md:flex items-center gap-8 text-sm">
                    <Link to="/" data-testid="nav-home" className="hover:text-[#FF6B35] transition-colors">Home</Link>
                    <Link to="/pricing" data-testid="nav-pricing" className="hover:text-[#FF6B35] transition-colors">Pricing</Link>
                    {user && <Link to="/dashboard" data-testid="nav-dashboard" className="hover:text-[#FF6B35] transition-colors">Dashboard</Link>}
                </nav>

                <div className="flex items-center gap-2">
                    {installPrompt && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    data-testid="install-app-btn"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleInstall}
                                    className="hidden sm:inline-flex"
                                >
                                    <Download className="h-4 w-4 mr-2" /> Install App
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Install Magnidrop as a desktop app</TooltipContent>
                        </Tooltip>
                    )}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                data-testid="theme-toggle-btn"
                                variant="ghost"
                                size="icon"
                                onClick={toggle}
                                aria-label="Toggle theme"
                            >
                                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}</TooltipContent>
                    </Tooltip>
                    {user ? (
                        <div className="flex items-center gap-2">
                            {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-border" />}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={() => logout?.()}>
                                        <LogOut className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Sign out</TooltipContent>
                            </Tooltip>
                        </div>
                    ) : (
                        <Button
                            data-testid="header-signin-btn"
                            className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white"
                            size="sm"
                            onClick={() => nav('/login')}
                        >
                            Sign in
                        </Button>
                    )}
                </div>
            </div>
        </header>
    );
}
